/**
 * 页面 / 表格的**布局策略**（纯值 + 纯函数）。
 *
 * 用户 2026-10-04 第三轮第 1、2 条：
 *  1.「现在可显示区域过宽，要把页面收窄一些，不要自动扩展」→ 内容区最大宽度；
 *  2.「从日预算到加购数中间间距搞到最小」→ 数值列的左右内边距。
 *
 * 为什么单独抽一个模块而不是在组件里写死 class：
 *  · 这两条都是**用户明确给出的量化口径**（宽度上限、间距下限），必须能被机器复核；
 *  · 表头 / 数据行 / 骨架行 / 组底汇总行必须用**同一套**内边距，否则列会错位
 *   （右对齐的数字会因为右侧内边距不同而看不到一条竖线上）。
 *
 * ⚠️ 只有「数值列」收紧；名称列与「语言 / 货号」列刻意保持呼吸空间（用户明确要求）。
 *
 * ## 2026-10-04 第十二批（用户第 2、3 条）改了什么
 *
 * **第 2 条「列头和数值没对准」**：根因有三处（都在本文件与 `AdTreeTable` 里）：
 *  1. **历史单元格的按钮内边距**：可点击的花费 / 4 个 ROAS 单元格渲染成
 *     `<button class="w-full px-0.5 text-right">`，比非按钮的数值单元格多 2px 右侧内边距
 *     → 同一列的「有历史按钮」和「没历史按钮」两种单元格右边缘差 2px；
 *  2. **同比变化量的宽度是内容决定的**：`↑ 12.4%` 与 `↓ 3.2%` 宽度不同，导致
 *     **同一列不同行**的数值右边缘左右漂移；
 *  3. **表头没有为变化量留位**：数值因为右侧要放变化量而左移 44px，表头却仍贴右边缘
 *     → 表头与数值相差约 44px。
 *
 *  修法（见 `AdTreeTable` 的 `renderMetricCell` 与 `DeltaSlot`）：
 *  · 历史按钮改成 `-mx-0.5 w-full px-0.5`（内容右边缘 = 单元格内容右边缘，hover 底色仍在）；
 *  · 同比变化量放进**固定宽度槽**（`COMPARISON_SLOT_PX = 44`）→ 数值右边缘在所有行一致；
 *  · 有同比的列（第十三批起只剩 ROAS(选定)；花费列已取消同比）**表头也留出同一个槽**
 *    （`COMPARISON_HEADER_PADDING_CLASS`）→ 表头文字右边缘 = 数值右边缘，变化量落在这段保留槽里。
 *
 * **第 3 条「加购数看不到了」**：选 (b) 压列宽（不是抬高 `--content-max-width`）。
 * 目标 = 1440px 视口（−232 侧栏 −10 滚动条 = **1198px**）下一屏看到加购数、无横向滚动。
 * 列宽分配表见 `AD_TREE_COLUMN_WIDTH_ESTIMATES`（第十三批起自然宽度 **1138px**）。
 */

import type { AdTreeColumnId } from "./adManagement.js";

/**
 * 页面内容区最大宽度（px）。
 *
 * ⚠️ 2026-10-04 第十二批的结论：**这个上限不是「加购数看不到」的原因，也不去动它**。
 * 真正的原因是「1440px 视口 − 232px 侧栏 − 10px 滚动条 = **1198px**」的可用宽度小于
 * 13 个数据列 + 名称列的自然需要宽度（改造前 1549px）→ 表格横向滚动，最后一列被推出屏幕。
 * 因此第十二批选的是**压缩列宽**（用户给的 (b) 方案），把自然宽度压到
 * **1182px ≤ 1198px**；第十三批（用户第 3 条）取消花费列的同比槽后进一步降到
 * **1138px**（算式见 `estimateAdTreeNaturalWidthPx` 与 `tableLayout.spec.ts`）。
 *
 * 上限保持 1600：宽屏（≥ 1830px 视口）下表格仍按 1600 排布，多出来的宽度由
 * **名称列**吸收（`min 156 / max 640`，可吸收 484px ≥ slack 418px）。
 *
 * ⚠️ 真实生效的值定义在 `styles/index.css` 的 `--content-max-width`（CSS 与 TS 都要用），
 * 这里只是它的 TS 镜像；`tableLayout.spec.ts` 会读 CSS 核对两者一致。
 */
export const CONTENT_MAX_WIDTH_PX = 1600;

/**
 * 内容区容器 class：`mx-auto`（居中）+ `w-full`（窄屏自适应）+ 上限。
 * 只有这一个地方定义容器写法，AppShell 直接引用，不允许别处再写一遍。
 */
export const CONTENT_MAX_WIDTH_CLASS = "mx-auto w-full max-w-[var(--content-max-width)]";

/**
 * 数值列左右内边距 = 4px。
 *
 * 为什么是 4px 而不是 0 / 2px：相邻两列的右/左内边距相加就是两个数字之间的最小间隔
 * （4 + 4 = 8px），再小就会让 `$1,234.56` 这类串读成一体（用户同时要求「不要让数字贴在一起」）。
 * 4px 是这条底线与「搞到最小」之间唯一同时成立的值，也是 4px 栅格的最小刻度。
 *
 * ⚠️ 第十二批起表头与**数据单元格**的内边距**分开定义**：数据单元格仍 `pl-1 pr-1`；
 * 有同比的列**表头多留 44px 槽**（`adTreeHeaderPaddingClass`）。两侧共用同一个函数
 * 已经不可能（表头要比数据多留槽），因此拆成 `adTreeCellPaddingClass` /
 * `adTreeHeaderPaddingClass` —— 两者都只用字面量 class（Tailwind 才扫得到）。
 */
export const NUMERIC_CELL_PADDING_X_PX = 4;
export const NUMERIC_CELL_PADDING_CLASS = "pl-1 pr-1";

/** 「语言 / 货号」列（chip 需要并排 + 换行）保留 8px；名称列用密度化的 `--cell-pad-x`。 */
export const LANGUAGE_CELL_PADDING_CLASS = "px-2";
export const NAME_CELL_PADDING_CLASS = "px-cell-x";

/**
 * **最后一列（加购数）的额外右侧留白**（2026-10-04 第八批 · 用户第 3 条
 * 「最右边留白少了一些，稍微加宽一点留白（加购数后边）」）。
 *
 * 取 12px：视觉上明显比原来的 4px 松弛，同时自然宽度只增加 8px
 * （左 4 + 右 12 = 16，原为 4 + 4 = 8）。第十二批压列宽后**保留这 12px**（确认项不回退）。
 *
 * ⚠️ class 必须是**字面量**（`pl-1 pr-3`），否则 Tailwind 构建扫描不到。
 */
export const LAST_COLUMN_RIGHT_PADDING_PX = 12;
export const LAST_COLUMN_PADDING_CLASS = "pl-1 pr-3";

/** 最后一列的 id（`addToCart`；`tableLayout.spec.ts` 会核对它确实是 `AD_TREE_COLUMNS` 的末项）。 */
export const LAST_COLUMN_ID: AdTreeColumnId = "addToCart";

/** 「语言 / 货号」是唯一一个左对齐、可换行、且不参与紧凑化的数据列。 */
const LANGUAGE_COLUMN_ID: AdTreeColumnId = "languageStyleCodes";

/**
 * **有同比变化量的列**（与 `AD_TREE_COLUMNS[].comparison === "shown"` 一一对应，
 * `tableLayout.spec.ts` 会核对）。
 *
 * 2026-10-04 第十三批（用户第 3 条）：花费列取消同比 → 只剩 `ROAS(选定)` 一列。
 *
 * 为什么在布局层再列一次：表头要为它留槽，而 `adTreeHeaderClass` 只拿到 `columnId`
 * （调用方传的是 `{ id, align }` 字面量，不带 `comparison`）。
 */
export const COMPARISON_COLUMN_IDS: readonly AdTreeColumnId[] = ["roasSelected"];

/** 该列是否有同比变化量（决定表头是否留槽）。 */
export function isComparisonColumn(columnId: AdTreeColumnId): boolean {
  return COMPARISON_COLUMN_IDS.includes(columnId);
}

/**
 * **同比变化量的固定槽宽 = 44px**（2026-10-04 第十二批新增）。
 *
 * 与 `AD_TREE_COMPARISON_SUFFIX_PX` 同值：槽里放的是 11px 的 `▲ 12.4%`（最坏 `▲ 100.0%`）。
 * 固定宽度是「表头与数值对齐」的关键——变化量宽度随内容变化，若不固定，
 * 同一列不同行的数值右边缘会左右漂移。
 */
export const COMPARISON_SLOT_PX = 44;
/** 固定槽的 class（必须字面量；`AdTreeTable` 用它渲染 `▲ 12.4%`）。 */
export const COMPARISON_SLOT_CLASS = "w-[44px]";

/**
 * 有同比两列的**表头右侧保留槽** = 槽宽 44 + 数据单元格右边距 4 = **48px**。
 *
 * 推导：数据单元格内容右边缘 `C = cellRight − 4`；行内布局 `[数值][槽 44]` 右对齐，
 * 因此**数值右边缘 = C − 44 = cellRight − 48**。表头文字要落在同一条线上，故
 * 表头 `padding-right = 48`。变化量本身落在 `[cellRight−44, cellRight]` 这段保留槽里。
 *
 * （槽内文字右对齐，`▲ 12.4%` ≈ 40px < 44px，左余量自然形成数值与变化量之间的间隙，
 *  因此不需要额外的 `ml-*`——少 4px 列宽。）
 */
export const COMPARISON_HEADER_PADDING_RIGHT_PX = 48;
/** 必须字面量（Tailwind 扫描）。 */
export const COMPARISON_HEADER_PADDING_CLASS = "pl-1 pr-[48px]";

/**
 * 「语言 / 货号」列的**最小宽度**。
 *
 * 2026-10-04 第十二批：368 → **128**。用户第 3 条明确把「语言 / 货号列的 `min-w-[368px]`」
 * 点名为可压缩对象；368px 是第五批为了让「`en` + 3 个货号 chip」不换行而定的**策略下限**，
 * 远高于该列的**内容最小宽度**（最宽的单个 chip ≈ 62px）。改成 128 后：
 *  · 宽度够放「语言 chip + 1 个货号 chip」；
 *  · 其余 chip 走既有的 `flex-wrap` 换行（不是截断、不是横向滚动）；
 *  · 腾出的 240px 正是让 1440px 下能看到加购数的关键。
 *
 * ⚠️ 值必须同时反映在 `AD_TREE_COLUMN_WIDTH_ESTIMATES` 的 `languageStyleCodes.contentPx`
 * （+16px 内边距 = 本值），`tableLayout.spec.ts` 会核对；class 必须是**字面量**。
 */
export const LANGUAGE_CELL_MIN_WIDTH_PX = 128;
export const LANGUAGE_CELL_WIDTH_CLASS = "min-w-[128px]";

export interface AdTreeColumnLike {
  readonly id: AdTreeColumnId;
  readonly align: "left" | "right";
}

/**
 * 列 → **数据单元格**左右内边距 class（数值列最紧；名称列与语言列不压；最后一列右侧多留白）。
 */
export function adTreeCellPaddingClass(columnId: AdTreeColumnId): string {
  if (columnId === LANGUAGE_COLUMN_ID) return LANGUAGE_CELL_PADDING_CLASS;
  // 第八批（用户第 3 条）：最后一列（加购数）右侧留白 12px，左侧仍是数值列的 4px。
  if (columnId === LAST_COLUMN_ID) return LAST_COLUMN_PADDING_CLASS;
  return NUMERIC_CELL_PADDING_CLASS;
}

/**
 * 列 → **表头**左右内边距 class（第十二批起与数据单元格分开：有同比的列多留 44px 槽）。
 *
 * 只有「有同比变化量」的列不同（第十三批起只剩 ROAS(选定)）；其余列与数据单元格逐字一致。
 */
export function adTreeHeaderPaddingClass(columnId: AdTreeColumnId): string {
  if (isComparisonColumn(columnId)) return COMPARISON_HEADER_PADDING_CLASS;
  return adTreeCellPaddingClass(columnId);
}

/** 列 → 数据单元格左右内边距合计（px，估算用；与上面的 class 必须一致）。 */
export function adTreeColumnPaddingPx(columnId: AdTreeColumnId): number {
  if (columnId === LANGUAGE_COLUMN_ID) return 16;
  if (columnId === LAST_COLUMN_ID) return NUMERIC_CELL_PADDING_X_PX + LAST_COLUMN_RIGHT_PADDING_PX;
  return NUMERIC_CELL_PADDING_X_PX * 2;
}

/** 列 → 表头左右内边距合计（px，估算用；有同比的列 = 4 左 + 52 右 = 56）。 */
export function adTreeHeaderPaddingPx(columnId: AdTreeColumnId): number {
  if (isComparisonColumn(columnId)) return NUMERIC_CELL_PADDING_X_PX + COMPARISON_HEADER_PADDING_RIGHT_PX;
  return adTreeColumnPaddingPx(columnId);
}

/** 列 → 宽度下限 class（只有「语言 / 货号」有：它要 guarantee「不换行」优先）。 */
export function adTreeColumnWidthClass(columnId: AdTreeColumnId): string {
  return columnId === LANGUAGE_COLUMN_ID ? LANGUAGE_CELL_WIDTH_CLASS : "";
}

/**
 * 数据单元格 class。
 *
 * `whitespace-nowrap` + `tnum` 保留（否则收紧内边距后数字会换行 / 抖动）；
 * 「语言 / 货号」列例外（chip 要能换行，不然窄屏只能横向滚动），
 * 但它有 `min-w-[128px]`：先保证一行放得下，真放不下才换行。
 */
export function adTreeCellClass(column: AdTreeColumnLike): string {
  return [
    "border-b border-line-subtle py-1 tnum",
    adTreeCellPaddingClass(column.id),
    adTreeColumnWidthClass(column.id),
    column.align === "right" ? "text-right" : "text-left",
    column.id === LANGUAGE_COLUMN_ID ? "whitespace-normal" : "whitespace-nowrap",
  ]
    .filter((part) => part.length > 0)
    .join(" ");
}

/**
 * 表头单元格 class（与数据单元格**同一套对齐**；内边距在第十二批起对有同比的列多留槽）。
 *
 * 表头 `whitespace-nowrap`：列宽的最坏情况由表头决定，因此窄列也只会把表撑宽
 * （走表格自身的横向滚动），而不是折成两行 / 溢出被裁掉。这也是「必要时缩短标签」
 * 之外的第二道保险。
 */
export function adTreeHeaderClass(column: AdTreeColumnLike): string {
  return [
    "border-b border-line bg-sunken py-2 font-medium align-bottom leading-tight whitespace-nowrap",
    adTreeHeaderPaddingClass(column.id),
    adTreeColumnWidthClass(column.id),
    column.align === "right" ? "text-right" : "text-left",
  ]
    .filter((part) => part.length > 0)
    .join(" ");
}

// ------------------------------------------------- 列宽核算（估算，可机器重算）

/**
 * 名称列的最小/最大宽度（与 `AdTreeTable` 的 `NAME_CELL_WIDTH` 是同一个口径）。
 *
 * 第十二批：`320/760 → 156/640`。名称列是本页唯一可以「少显示一点、悬浮看全」的列
 * （`flex-1 truncate` + tooltip 里有完整名称），因此压它最划算；同时把上限抬到 640，
 * 让宽屏下多出来的宽度有地方去（slack 418px ≤ 可吸收 484px，不会摊到数值列上）。
 * 名称单元格改成 `flex-wrap`（第十二批）：列窄时状态/类型 chip 换到第二行，
 * **标题不会**被 chip 挤没（这是第一批修过的 bug，不回退）。
 */
export const NAME_CELL_MIN_WIDTH_PX = 156;
export const NAME_CELL_MAX_WIDTH_PX = 640;

/**
 * 单列宽度的**估算**（`估算`，不是实测：本仓库没有浏览器量测）。
 *
 * 算式（spec 会重算，任何人改列必须同步这张表）：
 *
 *   表头宽 = 表头文本宽 + 排序箭头 + **表头内边距**
 *   内容宽 = 典型内容宽 + 同比槽 + **数据单元格内边距**
 *   列宽   = max(表头宽, 内容宽, 40 + 数据单元格内边距)
 *
 * 假设（写清楚以免被当成实测）：
 *  · 表头 = 12px、数据 = 14px（第七批字号 +1，不回退）；
 *  · 拉丁字符 ≈ 7.2px、数字（等宽）≈ 8.4px、中文字/全角括号 ≈ 12px（12px 表头档）；
 *    表头取**不换行**后的整串宽度（`adTreeHeaderClass` 已 nowrap）；
 *  · 内容取「最宽的真实形态」：金额形如 `$12,345.67`（75px）、比率形如 `3.42`、
 *    计数形如 `1,234`、货号列 = 语言 chip + 货号 chip（可换行，最小宽度是策略值）。
 *
 * 第十二批重算（用户第 2、3 条）：
 *  · **表头内边距**从 8 抬到 56（有同比的列：4 左 + 52 右，右侧是变化量槽）；
 *  · **语言 / 货号**列的内容策略宽从 352 → **112**（+16 = 128）；
 *  · 其余列的内容宽按上表重新核对（ROAS 用更真实的 `3.42`、CPA/CPC 用 `$12.34`）。
 */
export interface AdTreeColumnWidthEstimate {
  readonly id: AdTreeColumnId;
  /** 表头**文本**宽（px，不含排序箭头）。 */
  readonly headerPx: number;
  /** 典型内容宽（px，不含同比后缀）。 */
  readonly contentPx: number;
  /**
   * 排序方向箭头占的宽度（2026-10-04 第七批 · 交付物 D）。
   * `AD_TREE_SORT_ARROW_PX`（排序列）或 0（非排序列）——由 `AD_TREE_COLUMNS[].sortBasis`
   * 决定，`tableLayout.spec.ts` 会核对两者一一对应。
   */
  readonly sortArrowPx: number;
  /**
   * 同比变化量后缀占的宽度（2026-10-04 第七批 · 交付物 C；第十二批起 = 固定槽 44px，
   * 且表头也留同一个槽）。`COMPARISON_SLOT_PX`（显示变化量的列）或 0（其余列）。
   */
  readonly comparisonPx: number;
}

/**
 * 表头排序箭头（`↓`/`↑`）的估算宽度：12px 字号下 ≈ 7px 字形 + 2px 间隙 ≈ 10px。
 * 只影响两个排序列，且只在**当前排序**那一列渲染 —— 估算按最坏情况（有箭头）算。
 */
export const AD_TREE_SORT_ARROW_PX = 10;

/**
 * 同比变化量槽的估算宽度（第十二批起与 `COMPARISON_SLOT_PX` 同值，保留旧名字以免破坏调用方）。
 *
 * `▲ 12.4%` 在 11px（`text-2xs`）下：箭头 ≈ 7、间隙 ≈ 3、4 个等宽数字 ≈ 24、`%` ≈ 6
 * ⇒ 向上取整到 **44**（含 `▲ 100.0%` 的最坏情况）。
 */
export const AD_TREE_COMPARISON_SUFFIX_PX = 44;

/** 40px 的内容下限（表头文本短、内容也短的列不会细成一条线）。 */
export const AD_TREE_COLUMN_MIN_CONTENT_PX = 40;

/**
 * 列宽估算表（**估算**，不是实测：本仓库没有浏览器量测）。
 *
 * 第十二批的九列数值；第十三批（用户第 3 条）花费列取消同比槽后自然宽度从
 * 1182px 降到 1138px（算式与新论证见 `estimateAdTreeNaturalWidthPx` 的注释
 * 与 `tableLayout.spec.ts`）。
 */
export const AD_TREE_COLUMN_WIDTH_ESTIMATES: readonly AdTreeColumnWidthEstimate[] = [
  { id: "languageStyleCodes", headerPx: 60, contentPx: 112, sortArrowPx: 0, comparisonPx: 0 },
  { id: "deliveryDays", headerPx: 48, contentPx: 26, sortArrowPx: 0, comparisonPx: 0 },
  { id: "dailyBudget", headerPx: 36, contentPx: 75, sortArrowPx: AD_TREE_SORT_ARROW_PX, comparisonPx: 0 },
  { id: "spend", headerPx: 84, contentPx: 75, sortArrowPx: AD_TREE_SORT_ARROW_PX, comparisonPx: 0 },
  { id: "roasSelected", headerPx: 61, contentPx: 34, sortArrowPx: 0, comparisonPx: AD_TREE_COMPARISON_SUFFIX_PX },
  { id: "roasAsset", headerPx: 61, contentPx: 28, sortArrowPx: 0, comparisonPx: 0 },
  { id: "roasLifetime", headerPx: 61, contentPx: 34, sortArrowPx: 0, comparisonPx: 0 },
  { id: "roas30d", headerPx: 62, contentPx: 34, sortArrowPx: 0, comparisonPx: 0 },
  { id: "roas7d", headerPx: 56, contentPx: 34, sortArrowPx: 0, comparisonPx: 0 },
  { id: "cpa", headerPx: 22, contentPx: 46, sortArrowPx: 0, comparisonPx: 0 },
  { id: "cpc", headerPx: 22, contentPx: 46, sortArrowPx: 0, comparisonPx: 0 },
  { id: "purchases", headerPx: 36, contentPx: 39, sortArrowPx: 0, comparisonPx: 0 },
  { id: "addToCart", headerPx: 36, contentPx: 39, sortArrowPx: 0, comparisonPx: 0 },
];

/**
 * 某列估算宽度 = max(表头(文本+箭头+表头内边距), 内容(文本+槽+单元格内边距), 下限+单元格内边距)。
 *
 * 箭头加在表头侧、同比槽同时加在**表头与内容**两侧（第十二批）——两者都可能成为瓶颈。
 */
export function estimateAdTreeColumnWidthPx(estimate: AdTreeColumnWidthEstimate): number {
  const headerTotal = estimate.headerPx + estimate.sortArrowPx + adTreeHeaderPaddingPx(estimate.id);
  const contentTotal = estimate.contentPx + estimate.comparisonPx + adTreeColumnPaddingPx(estimate.id);
  const floorTotal = AD_TREE_COLUMN_MIN_CONTENT_PX + adTreeColumnPaddingPx(estimate.id);
  return Math.max(headerTotal, contentTotal, floorTotal);
}

/**
 * 表格的**自然需要宽度** = 13 个数据列 + 名称列最小宽度 + 名称单元格左右内边距（16px）。
 *
 * 第十二批的目标：**≤ 1198px**，即「1440px 视口 − 232px 侧栏 − 10px 滚动条」。
 * 第十三批（用户第 3 条）花费列取消同比槽后：
 * ```text
 *   13 个数据列 = 1138 − 156 − 16 = 966
 *   名称列最小 = 156
 *   名称内边距 = 16
 *   ⇒ 自然宽度 = 1138 ≤ 1198  ✓（余量 60px，比第十二批更宽裕）
 * ```
 * 宽屏上限 1600 与自然宽度的差额由名称列吸收（156 → 640）。
 */
export function estimateAdTreeNaturalWidthPx(): number {
  const columns = AD_TREE_COLUMN_WIDTH_ESTIMATES.reduce(
    (total, estimate) => total + estimateAdTreeColumnWidthPx(estimate),
    0,
  );
  return columns + NAME_CELL_MIN_WIDTH_PX + 16;
}

/**
 * **1440px 视口下表格的可用宽度**（第十二批新增，纯函数、可复核）。
 *
 * `viewport − sidebar − scrollbar`。侧栏宽度取 token 的默认值 232（`--sidebar-width`）；
 * 竖滚动条取 `.scroll-pane` 的 `scrollbar-width: thin` ≈ 10px。
 * 「自然宽度 ≤ 本值」= 一屏看到加收数、不出现横向滚动。
 */
export const SIDEBAR_WIDTH_PX = 232;
export const SCROLLBAR_WIDTH_PX = 10;
export const TARGET_VIEWPORT_WIDTH_PX = 1440;

export function availableTableWidthPx(
  viewportPx: number = TARGET_VIEWPORT_WIDTH_PX,
  sidebarPx: number = SIDEBAR_WIDTH_PX,
  scrollbarPx: number = SCROLLBAR_WIDTH_PX,
): number {
  return viewportPx - sidebarPx - scrollbarPx;
}
